// Turns a shared video-provider link (YouTube, Vimeo) into an embeddable iframe URL.

type EmbedFlags = { muted?: boolean; loop?: boolean; autoplay?: boolean };

// --- YouTube -------------------------------------------------------------
// Matches watch?v=, youtu.be/, /embed/ and /shorts/ links, capturing the 11-char video id.
const YOUTUBE_RE = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i;

export const getYouTubeId = (url: string): string | null => {
    if (!url) return null;
    return url.match(YOUTUBE_RE)?.[1] ?? null;
};

// Returns a privacy-friendly (nocookie) embed URL, or null if the input isn't a YouTube link.
export const getYouTubeEmbedUrl = (url: string, flags: EmbedFlags = {}): string | null => {
    const id = getYouTubeId(url);
    if (!id) return null;

    const params = new URLSearchParams();
    if (flags.autoplay) params.set("autoplay", "1");
    if (flags.muted) params.set("mute", "1");
    if (flags.loop) {
        params.set("loop", "1");
        params.set("playlist", id); // loop requires playlist=id on the embed player
    }
    const query = params.toString();
    return `https://www.youtube-nocookie.com/embed/${id}${query ? `?${query}` : ""}`;
};

// --- Vimeo ---------------------------------------------------------------
// Matches vimeo.com/<id>, player.vimeo.com/video/<id>, channels/groups paths, and an
// optional unlisted hash (either /<hash> in the path or ?h=<hash> in the query).
const VIMEO_RE = /vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d+)(?:\/([\da-zA-Z]+))?/i;

export const getVimeoEmbedUrl = (url: string, flags: EmbedFlags = {}): string | null => {
    if (!url) return null;
    const match = url.match(VIMEO_RE);
    if (!match) return null;

    const id = match[1];
    const hash = match[2] ?? url.match(/[?&]h=([\da-zA-Z]+)/)?.[1];

    const params = new URLSearchParams();
    if (hash) params.set("h", hash);
    if (flags.autoplay) params.set("autoplay", "1");
    if (flags.muted) params.set("muted", "1");
    if (flags.loop) params.set("loop", "1");
    params.set("dnt", "1"); // do-not-track, privacy-friendly

    return `https://player.vimeo.com/video/${id}?${params.toString()}`;
};

// --- Dispatcher ----------------------------------------------------------
// Returns an embed URL for any supported provider, or null for a plain file URL (MP4, etc.).
export const getEmbedUrl = (url: string, flags: EmbedFlags = {}): string | null =>
    getYouTubeEmbedUrl(url, flags) ?? getVimeoEmbedUrl(url, flags);
